import { useId } from 'react';

interface UseFieldMessageParams {
  /** Ids of other elements already describing the field */
  describedBy?: string;
  /** The error shown under the field */
  error?: string;
  /** The help text shown under the field */
  help?: string;
  /** The id passed to the field, a stable one is generated otherwise */
  id?: string;
  /** Whether the field is required */
  required?: boolean;
}

/**
 * Accessibility wiring for a form field and the message under it:
 * - a stable id, so the label is always linked to the field
 * - the message linked as the field's description, read when it gets focus
 * - the field marked as invalid and required
 * - the message in a live region that is always rendered, so an error is
 *   announced as soon as it appears (e.g. when the form is submitted)
 */
const useFieldMessage = ({
  describedBy,
  error,
  help,
  id,
  required,
}: UseFieldMessageParams) => {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const messageId = `${fieldId}-message`;
  const hasMessage = !!(error || help);

  return {
    fieldId,
    fieldProps: {
      'aria-describedby':
        [describedBy, hasMessage ? messageId : null]
          .filter(Boolean)
          .join(' ') || undefined,
      'aria-invalid': error ? true : undefined,
      'aria-required': required ? true : undefined,
      id: fieldId,
    },
    messageProps: {
      'aria-live': 'polite' as const,
      id: messageId,
    },
  };
};

export default useFieldMessage;
